import { CircularProgress, Stack, Typography } from '@mui/material';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { setModalToDisplay } from '../../../core/legacy/actions/modals';
import { ReferenceModalTypes } from '../../../core/legacy/reducers/modals';
import { buildGenerationStatus } from '../../../core/reference/generationStatus';
import {
    isReferenceGenerationActiveQuery,
    latestReferenceGenerationQuery,
    referenceGenerationErrorQuery,
    roadSectionsQuery,
} from '../../../core/reference/queries';
import { useAppDispatch, useAppSelector } from '../../../types';
import BaseButton from '../../design_system/inputs/buttons/BaseButton';
import { theme } from '../../legacy/style/theme';

interface GenerateReferenceProps {
    /** The status line is always shown; the button can be hidden when another one is on screen. */
    showButton?: boolean;
    /** Puts the status on the left of the button, for a control aligned to the right edge. */
    statusFirst?: boolean;
}

const formatDate = (isoDate: string): string => new Date(isoDate).toLocaleDateString('fr-FR');

/** Button that generates the référentiel from the organization's sessions, with its status line. */
export const GenerateReference: FC<GenerateReferenceProps> = ({ showButton = true, statusFirst = false }) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const latest = useAppSelector(latestReferenceGenerationQuery);
    const isActive = useAppSelector(isReferenceGenerationActiveQuery);
    const error = useAppSelector(referenceGenerationErrorQuery);
    const hasReference = useAppSelector(roadSectionsQuery).length > 0;

    const status = buildGenerationStatus({ latest, requestOngoing: isActive, error, hasReference });

    const renderStatus = () => {
        switch (status?.kind) {
            case 'running':
                return (
                    <Stack direction="row" alignItems="center" gap={1}>
                        <CircularProgress size={16} />
                        <Typography sx={{ fontSize: '14px', color: theme.color.blue.primary3 }}>
                            {t(
                                status.firstGeneration
                                    ? 'reference.generation.status.runningFirst'
                                    : 'reference.generation.status.runningNext',
                            )}
                        </Typography>
                    </Stack>
                );
            case 'error':
                return <StatusText problem text={t(`reference.generation.error.${status.reason}`)} />;
            case 'failed':
                return <StatusText problem text={t('reference.generation.status.failed')} />;
            case 'done': {
                const date = formatDate(status.date);
                if (status.added === null) return <StatusText text={t('reference.generation.status.done', { date })} />;
                if (status.added === 0)
                    return <StatusText text={t('reference.generation.status.doneNone', { date })} />;

                return <StatusText text={t('reference.generation.status.doneCount', { date, count: status.added })} />;
            }
            default:
                return null;
        }
    };

    return (
        <Stack direction={statusFirst ? 'row-reverse' : 'row'} alignItems="center" gap={2}>
            {showButton && (
                <BaseButton
                    variant="primary"
                    disabled={status?.kind === 'running'}
                    onClick={() => dispatch(setModalToDisplay(ReferenceModalTypes.REFERENCE_GENERATION))}
                >
                    {t('reference.generation.button')}
                </BaseButton>
            )}
            {renderStatus()}
        </Stack>
    );
};

const StatusText: FC<{ text: string; problem?: boolean }> = ({ text, problem = false }) => (
    <Typography sx={{ fontSize: '14px', color: problem ? theme.color.red.primary : theme.color.grey.dark }}>
        {text}
    </Typography>
);
